import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { type ListingApplicationRowFragment } from "@/graphql/generated/operations";
import { communicationLanguageName } from "@/lib/communication-language";
import { formatDateTime } from "@/lib/format-date";
import { useRequestPowerQuiz } from "./use-request-power-quiz";

type Application = ListingApplicationRowFragment;
export function QuizRequestStatus({ request }: { request: Application["powerQuizRequest"] }) {
  const intl = useIntl();
  if (!request) return null;
  return <p role="status" className="text-smycles-meta text-ink-muted">
    {intl.formatMessage({ id: request.status === "SENT" ? "share.listings.quiz.sent"
      : request.status === "SENDING" ? "share.listings.quiz.sending"
      // FAILED reached no mail server and can be requested anew; only UNCERTAIN may have arrived (GENESIS-0081).
      : request.status === "FAILED" ? "share.listings.quiz.failed" : "share.listings.quiz.uncertain" },
      { date: formatDateTime(request.sentOn ?? request.requestedOn, intl.locale) })}
    {/* The language the request fixed when it was reserved; requests from before that have none. */}
    {request.communicationLanguage ? ` ${intl.formatMessage({ id: "share.listings.quiz.requestLanguage",
      defaultMessage: "Language: {language}" },
      { language: communicationLanguageName(intl, request.communicationLanguage) })}` : null}
  </p>;
}


export function ListingPowerQuizRequest({ application }: { application: Application }) {
  const intl = useIntl();
  const [confirm, setConfirm] = useState(false);
  const { send, loading, error } = useRequestPowerQuiz(application.id);
  const previous = application.powerQuizRequest;
  const label = intl.formatMessage({ id: previous ? "share.listings.quiz.resend" : "share.listings.quiz.request" });
  return <div className="space-y-2">
    <QuizRequestStatus request={previous} />
    {application.actions.canRequestPowerQuiz ? <Button id={`listing-quiz-request-${application.id}`} size="compact" variant="secondary"
      disabled={loading || previous?.status === "SENDING"} onClick={() => setConfirm(true)}>{label}</Button> : null}
    {error ? <p role="alert" className="text-smycles-meta text-ink-accent">{error}</p> : null}
    <ConfirmDialog id={`listing-quiz-confirm-${application.id}`} open={confirm} title={label} destructive={false}
      message={`${intl.formatMessage({ id: previous ? "share.listings.quiz.resendHint" : "share.listings.quiz.requestHint" })} ${
        application.communicationLanguage
          ? intl.formatMessage({ id: "share.listings.quiz.languageHint",
            defaultMessage: "The email is written in {language}, the language that applies to this application." },
          { language: communicationLanguageName(intl, application.communicationLanguage) })
          : intl.formatMessage({ id: "share.listings.quiz.languageUnknown",
            defaultMessage: "The language of the email cannot be determined because the default email language is set incorrectly. Sending is refused." })}`}
      confirmLabel={intl.formatMessage({ id: "share.listings.quiz.send" })}
      onCancel={() => setConfirm(false)} onConfirm={async () => { setConfirm(false); await send(); }} />
  </div>;
}
